import { LanguageToCountry, SupportedLanguages } from "@/enums/internationalization";

const SUPPORTED_LOCALE_LANGUAGE_VALUES = [
  SupportedLanguages.English,
  SupportedLanguages.Spanish,
  SupportedLanguages.Portuguese,
  SupportedLanguages.Japanese,
  SupportedLanguages.German,
  SupportedLanguages.French,
] as const satisfies readonly SupportedLanguages[];

export type SupportedLocaleLanguage =
  (typeof SUPPORTED_LOCALE_LANGUAGE_VALUES)[number];

export const SUPPORTED_LOCALE_LANGUAGES: SupportedLanguages[] = [
  ...SUPPORTED_LOCALE_LANGUAGE_VALUES,
];

const SUPPORTED_LOCALE_LANGUAGE_CODES = new Set<string>(
  SUPPORTED_LOCALE_LANGUAGES
);

export const isSupportedLocaleLanguage = (
  language: unknown
): language is SupportedLocaleLanguage => {
  return (
    typeof language === "string" && SUPPORTED_LOCALE_LANGUAGE_CODES.has(language)
  );
};

export const normalizeLocaleLanguage = (
  language: string | null | undefined,
  fallback: SupportedLanguages = SupportedLanguages.English
): SupportedLocaleLanguage => {
  const baseLanguage = language?.trim().toLowerCase().split("-")[0];

  if (isSupportedLocaleLanguage(baseLanguage)) {
    return baseLanguage;
  }

  if (isSupportedLocaleLanguage(fallback)) {
    return fallback;
  }

  return SupportedLanguages.English;
};

const REGION_SUBTAG = /^[A-Za-z]{2}$/;

/**
 * Resolve a locale string to a *specific* culture (`language-REGION`) or `null`
 * when it carries no usable language. The BCP-47 parsing lives here, split out
 * of {@link normalizeLocalization}, so both the input and the fallback flow
 * through the exact same canonicalization.
 */
const toSpecificCulture = (
  locale: string | null | undefined
): string | null => {
  const trimmed = locale?.trim();
  if (!trimmed) {
    return null;
  }

  // Parse per BCP-47 so a script subtag (e.g. "zh-Hant-TW") doesn't shadow the
  // region the way a naive "second subtag is the region" split would. Intl.Locale
  // throws on malformed input — which we can receive from the request body — so
  // guard it and fall back to deriving from the leading language subtag.
  let parsed: Intl.Locale | undefined;
  try {
    parsed = new Intl.Locale(trimmed);
  } catch {
    // Malformed tag (e.g. "en_US" with an underscore); recover below.
  }

  const language = (parsed?.language ?? trimmed.split(/[-_]/)[0]).toLowerCase();
  if (!language) {
    return null;
  }

  const region = parsed?.region;
  if (region && REGION_SUBTAG.test(region)) {
    return `${language}-${region.toUpperCase()}`;
  }

  if (Object.hasOwn(LanguageToCountry, language)) {
    return `${language}-${LanguageToCountry[language as SupportedLanguages]}`;
  }

  return null;
};

/**
 * Normalize an arbitrary locale string into a *specific* culture of the form
 * `language-REGION` (e.g. "en-US").
 *
 * Galactus' `Localization` type rejects neutral cultures: a bare language tag
 * like "en" or "fr" — which `navigator.language`/`navigator.languages[0]` can
 * legitimately return when the browser locale has no country — throws
 * `ArgumentException` server-side and 500s org creation. When the input has no
 * usable region we derive one from `LanguageToCountry`.
 *
 * The result is *always* a specific culture: the `fallback` is itself run
 * through the same canonicalization, and a hardcoded "en-US" guards an unusable
 * fallback — so a neutral or malformed `fallback` can never reach the backend.
 *
 * Note: this returns a full culture (`language-REGION`) for the backend. For the
 * bare i18n language code the frontend translation layer expects, use
 * `normalizeLocaleLanguage` instead.
 */
export const normalizeLocalization = (
  locale: string | null | undefined,
  fallback = "en-US"
): string =>
  toSpecificCulture(locale) ?? toSpecificCulture(fallback) ?? "en-US";
